import { WebSocketRequest, WebSocketMessage } from '../../hooks/useWebSocket';
import {
  ListingSummaryResponse,
  ListingDetailResponse,
  ListingSearchResponse,
} from './types';

/**
 * Options for sending and processing listing search messages
 */
export interface SendMessageAndProcessOptions {
  /** Callback when summary response is received */
  onSummary?: (response: ListingSummaryResponse) => void;
  /** Callback when detail response is received */
  onDetail?: (response: ListingDetailResponse) => void;
  /** Callback when all responses are complete */
  onComplete?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Timeout in milliseconds (default: 30000ms = 30s) */
  timeoutMs?: number;
}

/**
 * Sends a WebSocket message and processes all progressive responses.
 *
 * This function handles the multi-response pattern used by listing search:
 * 1. Sends the initial request
 * 2. Processes the summary response (contains all listing keys)
 * 3. Processes detail responses as they arrive (one per listing)
 * 4. Handles completion and errors
 *
 * @example
 * ```typescript
 * await sendMessageAndProcess(
 *   webSocket,
 *   'SearchListings',
 *   { page: 1, perPage: 20, filters: { status: 'Active' } },
 *   {
 *     onSummary: (response) => {
 *       // Show skeleton cards
 *       setListings(response.summary.listings.map(l => ({ ...l, IsSummary: true })));
 *     },
 *     onDetail: (response) => {
 *       // Update specific card with full data
 *       updateListing(response.listing);
 *     },
 *     onComplete: () => {
 *       console.log('All listings loaded');
 *     },
 *     onError: (error) => {
 *       console.error('Failed to load listings:', error);
 *     }
 *   }
 * );
 * ```
 */
export async function sendMessageAndProcess(
  sendMessage: (
    request: WebSocketRequest,
    timeoutMs?: number,
  ) => Promise<WebSocketMessage>,
  action: string,
  payload: any,
  options: SendMessageAndProcessOptions = {},
): Promise<void> {
  const {
    onSummary,
    onDetail,
    onComplete,
    onError,
    timeoutMs = 30000,
  } = options;

  try {
    // Generate request ID
    const requestId = `${action}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Send the initial request
    const request: WebSocketRequest = {
      action,
      requestId,
      payload,
    };

    // For listing search, we need to handle multiple responses
    // We'll use a promise that resolves when we get the 'complete' response
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Request timed out waiting for responses'));
      }, timeoutMs);

      // Custom handler for progressive responses
      const handleResponse = (message: WebSocketMessage) => {
        try {
          // Check for errors
          if (message.type === 'error') {
            clearTimeout(timeout);
            const error = new Error(message.error?.message || 'Unknown error');
            if (onError) onError(error);
            reject(error);
            return;
          }

          // Parse the response data
          const data = message.data as ListingSearchResponse;

          if (!data || typeof data !== 'object' || !('status' in data)) {
            return; // Ignore malformed responses
          }

          // Handle different response types
          const status = (data as any).status;
          switch (status) {
            case 'listing_summary': {
              if (onSummary) {
                onSummary(data as ListingSummaryResponse);
              }
              break;
            }

            case 'listing_detail': {
              if (onDetail) {
                onDetail(data as ListingDetailResponse);
              }
              break;
            }

            case 'complete': {
              clearTimeout(timeout);
              if (onComplete) {
                onComplete();
              }
              resolve();
              break;
            }

            default:
              console.warn('Unknown response status:', status);
          }
        } catch (err) {
          clearTimeout(timeout);
          const error =
            err instanceof Error
              ? err
              : new Error('Failed to process response');
          if (onError) onError(error);
          reject(error);
        }
      };

      // Send the message
      // Note: This is a simplified version - in practice, you'd need to subscribe to the WebSocket
      // and handle multiple messages with the same requestId
      sendMessage(request, timeoutMs)
        .then((message) => {
          handleResponse(message);
        })
        .catch((err) => {
          clearTimeout(timeout);
          if (onError) onError(err);
          reject(err);
        });
    });
  } catch (err) {
    const error =
      err instanceof Error ? err : new Error('Failed to send message');
    if (onError) onError(error);
    throw error;
  }
}

/**
 * Parse URL search parameters into search state
 */
export function parseSearchParams(searchParams: URLSearchParams): {
  page: number;
  perPage: number;
  filters: {
    propertyType: string;
    status: string;
    search: string;
  };
} {
  return {
    page: parseInt(searchParams.get('page') || '1', 10),
    perPage: parseInt(searchParams.get('perPage') || '8', 10),
    filters: {
      propertyType: searchParams.get('propertyType') || '',
      status: searchParams.get('status') || '',
      search: searchParams.get('search') || '',
    },
  };
}

/**
 * Build URL search parameters from search state
 */
export function buildSearchParams(state: {
  page: number;
  perPage: number;
  filters: {
    propertyType: string;
    status: string;
    search: string;
  };
}): URLSearchParams {
  const params = new URLSearchParams();

  // Always include page and perPage for clarity
  params.set('page', state.page.toString());
  params.set('perPage', state.perPage.toString());

  // Only include filters if they're not default values
  if (state.filters.propertyType && state.filters.propertyType !== 'all') {
    params.set('propertyType', state.filters.propertyType);
  }

  if (state.filters.status && state.filters.status !== 'all') {
    params.set('status', state.filters.status);
  }

  if (state.filters.search) {
    params.set('search', state.filters.search);
  }

  return params;
}
